import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Camera,
    Film,
    Image as ImageIcon,
    Palette,
    Sparkles,
    GlassWater,
    Mic2,
    CalendarDays,
    Users,
    Heart,
    Layers,
    Zap,
    Building2
} from 'lucide-react';

const eventServices = [
    { id: '01', icon: Sparkles, title: 'Corporate Galas & Awards', desc: 'Capturing the prestige, keynote handshakes, high-profile steps, and black-tie ballroom atmospheres.' },
    { id: '02', icon: Mic2, title: 'Conferences & Keynotes', desc: 'Crisp speaker close-ups, dynamic wide-angle stage tracking, and high-energy audience reaction frames.' },
    { id: '03', icon: GlassWater, title: 'Brand Activations & Mixers', desc: 'Documenting experiential pop-ups, immersive VIP press rooms, and fast-paced cocktail networking dynamics.' },
    { id: '04', icon: Users, title: 'Festivals & Live Concerts', desc: 'Freezing rapid stage motion, complex performance lighting, and massive, high-energy crowd reactions.' },
    { id: '05', icon: Building2, title: 'Expositions & Trade Shows', desc: 'Clean, wide portfolio captures of grand architectural pavilions, showcase booths, and vendor interactions.' },
    { id: '06', icon: Film, title: 'Cinematic Event Afterfilms', desc: 'Dynamic, rapid-cut recap videos engineered perfectly for post-event marketing and future ticket promos.' },
];

const celebrationServices = [
    { id: '01', icon: Heart, title: 'Milestone Anniversaries', desc: 'Meticulous storytelling tracking generational gatherings, micro-expressions, and intimate toast moments.' },
    { id: '02', icon: Sparkles, title: 'Luxury Private Banquets', desc: 'High-end coverage for private luxury estates, curated birthday dinners, and exclusive society gatherings.' },
    { id: '03', icon: CalendarDays, title: 'Immersive Seasonal Galas', desc: 'Capturing custom seasonal transformations, grand holiday theme configurations, and charity fund drives.' },
    { id: '04', icon: Palette, title: 'Thematic Decor & Details', desc: 'Dedicated focus on custom floral architectures, lighting schemes, curated fine-dining tablescapes, and menus.' },
    { id: '05', icon: ImageIcon, title: 'Candid Interaction Tracking', desc: 'Unobtrusive photojournalistic coverage focused entirely on real emotion, unscripted laughs, and dance floors.' },
];

const comprehensiveEventDeliverables = [
    { id: '01', icon: Zap, title: 'Low-Light Sensor Architecture', desc: 'Utilizing premium wide-aperture lenses to capture sharp, noise-free images under dim, moody venue lighting.' },
    { id: '02', icon: Camera, title: 'Next-Day PR Photo Pulls', desc: 'Expedited delivery of high-priority media assets within 12 hours for rapid press release and social media pushes.' },
    { id: '03', icon: Users, title: 'Multi-Shooter Synchronicity', desc: 'Seamlessly coordinated multi-camera teams to cover massive dual-stage corporate conferences or sprawling properties.' },
    { id: '04', icon: Layers, title: 'Advanced Flash Color Gelling', desc: 'Balancing on-camera strobe warmth perfectly with venue accent lighting to prevent unnatural skin washouts.' },
    { id: '05', icon: Film, title: 'Vertical Social Reel Edits', desc: 'Delivering event highlight clips cropped explicitly in 9:16 vertical layouts for instant Instagram and TikTok traction.' },
    { id: '06', icon: Sparkles, title: 'Digital Step-and-Repeat Grid', desc: 'Perfectly balanced, repetitive portraits of arriving VIP guests against sponsor banner backdrops.' },
    { id: '07', icon: GlassWater, title: 'Catering & Mixology Stills', desc: 'Editorial food styling captures displaying signature cocktails and curated hors d\'oeuvres layouts.' },
    { id: '08', icon: CalendarDays, title: 'Full Timeline Redundancy', desc: 'Rigorous dual-card camera slot live backups ensuring your once-in-a-lifetime moments are instantly protected.' },
    { id: '09', icon: ImageIcon, title: 'Cloud-Based Attendee Portals', desc: 'High-speed, face-recognition enabled web galleries allowing event attendees to instantly find and grab their own media.' },
];

const EventsandCelebrations = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.6;
        const speedRight = 0.4;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            <Helmet>
                <title>Events & Celebrations Photography in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Professional Events & Celebrations Photography in Hyderabad by KhultStudios. We capture corporate events, birthdays, anniversaries, private parties, college events, award ceremonies, cultural events, concerts, and live celebrations with photography and cinematic videography."
                />

                <meta
                    name="keywords"
                    content="Event Photography Hyderabad, Event Videography Hyderabad, Birthday Photography, Anniversary Photography, Corporate Event Photography, Private Party Photography, Cultural Event Photography, College Event Photography, Award Ceremony Photography, Concert Photography, Live Event Photography, Celebration Photography, Stage Event Photography, Drone Event Photography, Event Photographer Hyderabad, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/events-and-celebrations-photography"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/events-and-celebrations-photography"
                />
                <meta
                    property="og:title"
                    content="Events & Celebrations Photography in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Professional photography and cinematic videography for birthdays, anniversaries, corporate events, concerts, college festivals, award ceremonies, and celebrations in Hyderabad."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/events-celebrations-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Events & Celebrations Photography | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Capture every special moment with professional event photography and videography services in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/events-celebrations-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/khultstudio.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Events & Celebrations Production
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Freezing the Raw Energy of Human Connection
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        An exceptional event takes months to orchestrate, but its finest moments flash by in milliseconds. At KhultStudio, we specialize in high-fidelity documentation that captures the scale, ambient luxury, and spontaneous emotions of your grandest gatherings.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Deploying high-speed, low-noise camera matrices, color-gelled flash setups, and a non-disruptive photojournalistic approach, we generate vibrant PR portfolios and high-impact afterfilms engineered to make your activations unforgettable.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Galleries
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-linkedin-in" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=500" alt="Corporate Conference Stage Keynote" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=500" alt="Luxury Gala Table Decor Setup" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=500" alt="High-Energy Live Performance Concert" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?q=80&w=500" alt="Indoor Brand Activation Mixer" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=500" alt="Corporate Conference Stage Keynote Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=500" alt="Luxury Gala Table Decor Setup Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=500" alt="High-Energy Live Performance Concert Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?q=80&w=500" alt="Indoor Brand Activation Mixer Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/events-and-celebrations/events-and-celebrations-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/events-and-celebrations/events-and-celebrations-photography2.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/events-and-celebrations/events-and-celebrations-photography3.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/events-and-celebrations/events-and-celebrations-photography4.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/events-and-celebrations/events-and-celebrations-photography5.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/events-and-celebrations/events-and-celebrations-photography6.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/events-and-celebrations/events-and-celebrations-photography7.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/events-and-celebrations/events-and-celebrations-photography8.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* EVENT PHOTOGRAPHY SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">🎤 Institutional & Large-Scale Event Media</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        High-profile summits, trade shows, and brand launches demand strict visual alignment. Our event production framework maps crowd density, captures brand signage flawlessly, and guarantees rapid asset turnover for your marketing loops.
                    </p>
                    <div className="row g-4 mt-2">
                        {eventServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 1 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CELEBRATIONS PHOTOGRAPHY SERVICES SECTION */}
            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">✨ Private Milestones & Luxury Celebrations</h2>
                        <p className="text-light-gray mt-3 max-w-2xl mx-auto">
                            Immersive private functions thrive on intimate, unrepeatable interactions. We operate seamlessly in the background, immortalizing complex design installations, luxury banquet details, and genuine emotional connection.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {celebrationServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMPREHENSIVE EVENT DELIVERABLES MATRIX */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">💼 Premium Media Operational Deliverables</h2>
                    <div className="row g-4">
                        {comprehensiveEventDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EventsandCelebrations
